import React, { FC, useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import BriefInfo from 'components/posts/detail/overview/header/BriefInfo';
import QuickActions from 'components/posts/detail/overview/header/QuickActions';
import { PostModalOnClose } from 'app/modules/posts/modules/post/hooks/usePostModal';
import { Icon, IconButton } from '@kontentino/ui';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import { faClose } from '@fortawesome/pro-regular-svg-icons/faClose';
import usePostDetailPagination from 'app/modules/posts/modules/post/hooks/usePostDetailPagination';
import KeyCodes from 'constants/keyCodes';
import { usePostQuery } from 'app/modules/posts/modules/post/hooks/usePostQuery';

type Props = {
  id: number;
  onClose: PostModalOnClose;
  onDuplicatePostClick: () => void;
};

const SWITCH_DISABLED_HTML_ELEMENTS = ['input', 'textarea'];
const SWITCH_DISABLED_CLASS_NAMES = ['lg-show'];

const Header: FC<Props> = (props) => {
  const {
    postDetail: { isLoading, data },
  } = usePostQuery(props.id);
  const { goNext, goPrev, canGoNext, canGoPrev } = usePostDetailPagination();

  useEffect(() => {
    const onKeyDownArrows = (e: KeyboardEvent) => {
      const tagName = (e?.target as HTMLElement)?.tagName?.toLowerCase();

      if (
        SWITCH_DISABLED_HTML_ELEMENTS.includes(tagName) ||
        SWITCH_DISABLED_CLASS_NAMES.some((className) =>
          (e?.target as HTMLElement).classList.contains(className),
        )
      ) {
        return;
      }

      if (e.key === KeyCodes.ArrowRight) {
        goNext();
      }
      if (e.key === KeyCodes.ArrowLeft) {
        goPrev();
      }
    };

    document.addEventListener('keydown', onKeyDownArrows);

    return () => {
      document.removeEventListener('keydown', onKeyDownArrows);
    };
  }, [goNext, goPrev]);

  return (
    <div className="tw-flex tw-items-center tw-px-6 tw-py-4">
      <div className="tw-mr-4 tw-flex">
        {isLoading && <Skeleton width="100%" height="24px" />}
        {data && <BriefInfo post={data} />}
      </div>
      <div className="tw-ml-auto tw-flex tw-items-center tw-gap-3">
        {data && (
          <QuickActions
            data={data}
            onDuplicatePostClick={props.onDuplicatePostClick}
            onClose={props.onClose}
          />
        )}
        <div className="tw-hidden tw-items-center lg:tw-flex">
          <IconButton
            data-cy="post-detail-header-previous-button"
            size="small"
            variant="secondary"
            disabled={!canGoPrev}
            icon={<Icon icon={faChevronLeft} />}
            onClick={goPrev}
            tabIndex={0}
          />
          <IconButton
            data-cy="post-detail-header-next-button"
            size="small"
            variant="secondary"
            disabled={!canGoNext}
            icon={<Icon icon={faChevronRight} />}
            onClick={goNext}
            tabIndex={0}
          />
        </div>
        <IconButton
          data-cy="post-preview-close-button"
          size="small"
          variant="secondary"
          icon={<Icon icon={faClose} />}
          onClick={() => props.onClose()}
        />
      </div>
    </div>
  );
};
export default Header;
